import React, { useState, useEffect, useContext } from "react";
import { Table, Typography, Card, Space, Button, notification, Tooltip } from "antd";
import { DownloadOutlined, RightOutlined, DownOutlined } from "@ant-design/icons";
import mainContext from "../context/MainContext/mainContext";
import PdfFormatJsx from "./PdfFormatJsx";
const { Title, Text } = Typography;
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const GeneratedCreditNotes = () => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);
  const { financialYear } = useContext(mainContext);

  const handleButtonClick = (record) => {
    setSelectedCreditNote(record);
  };


  useEffect(() => {
    fetchCreditNotes();
  }, [financialYear]);

  const fetchCreditNotes = async () => {
    try {
      setLoading(true);
      const url = new URL(`${apiBaseUrl}/inventoryManagement/getCreditNotes`);
      if (financialYear) url.searchParams.set("financialYear", financialYear);

      const response = await fetch(
        url.toString(),
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log("data",data);
        if (data.success) {
          setCreditNotes(data.data || []);
        } else {
          notification.error({
            message: data.message || "Failed to fetch credit notes",
          });
        }
      } else {
        notification.error({ message: "Failed to fetch credit notes" });
      }
    } catch (error) {
      notification.error({ message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const expandedRowRender = (record) => {
    const columns = [
      {
        children: [
          {
            title: () => (
              <Text strong style={{ color: "#2f54eb" }}>
                Code No
              </Text>

            ),
            dataIndex: ["invId", "CodeNo"],
            key: "codeNo",
            width: 120,
          },
          {
            title: () => (
              <Text strong style={{ color: "#722ed1" }}>
                Location
              </Text>
            ),
            dataIndex: ["invId", "locations", "name"],
            key: "location",
            width: 200,
            render: (text) => (
              <Tooltip title={text}>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {text}
                </div>
              </Tooltip>
            ),
          },
          // {
          //   title: () => (
          //     <Text strong style={{ color: "#13c2c2" }}>
          //      Special Rate
          //     </Text>
          //   ),
          //   dataIndex: ["specialRate"],
          //   key: "ratePerMonth",
          //   width: 120,
          //   align: "right",
          //   render: (value) => (
          //     <Text type="secondary">
          //       ₹{value?.toLocaleString("en-IN", { minimumFractionDigits: 0 })}
          //     </Text>
          //   ),
          // },
          {
            title: () => (
              <Text strong style={{ color: "#52c41a" }}>
                Amount
              </Text>
            ),
            dataIndex: "amount",
            key: "amount",
            width: 120,
            align: "right",
            render: (value) => (
              <Text strong style={{ color: "#52c41a" }}>
                ₹{value?.toLocaleString("en-IN", { minimumFractionDigits: 0 })}
              </Text>
            ),
          },
          {
            title: () => (
              <Text strong style={{ color: "#1890ff" }}>
                Start Date
              </Text>
            ),
            dataIndex: "startDate",
            key: "startDate",
            width: 120,
            align: "right",
            render: (date) => date ? new Date(date).toLocaleDateString("en-GB") : "-",
          },
          {
            title: () => (
              <Text strong style={{ color: "#faad14" }}>
                End Date
              </Text>
            ),
            dataIndex: "endDate",
            key: "endDate",
            width: 120,
            align: "right",
            render: (date) => date ? new Date(date).toLocaleDateString("en-GB") : "-",
          },
        ],
      },
    ];

    return (
      <div style={{ margin: "10px 10px", maxWidth: "1400px" }}>
        {record.salesOrderId?.stamp && (
          <div style={{ marginBottom: 8 }}>
            <Text strong>SO/Id : {record.salesOrderId.stamp}</Text>
          </div>
        )}
        <Table
          columns={columns}
          dataSource={(record.inventoryAgainst || []).filter((row) => row?.amount != null)}
          pagination={false}
          rowKey={(record) => record.invId?._id}
          bordered
          size="small"
          style={{
            borderRadius: "8px",
            marginTop: "8px",
            width: "100%",
          }}
          rowClassName={() => "child-row"}
          className="child-table"
        />
      </div>
    );
  };

  const columns = [
    {
      title: () => <span style={{ whiteSpace: "nowrap" }}>Credit Note No</span>,
      dataIndex: "CnNumber",
      key: "CnNumber",
      width: 160,
      ellipsis: true,
      fixed: "left",
      render: (text) => (
        <span style={{ whiteSpace: "nowrap" }} title={text}>
          {text}
        </span>
      ),
    },
    {
      title: "IRN",
      dataIndex: "IRN",
      key: "IRN",
      ellipsis: true,
      width: 200,
    },
    {
      title: "Ack No",
      dataIndex: "AckNumber",
      key: "AckNumber",
      width: 140,
    },
    {
      title: "Ack Date",
      dataIndex: "AckDate",
      key: "AckDate",
      width: 140,
    },
    {
      title: "Printing Charge",
      dataIndex: "printingCharge",
      key: "printingCharge",
      align: "right",
      width: 120,
      render: (charge) => (
        <Text type="secondary">
          ₹{charge?.toLocaleString("en-IN", { minimumFractionDigits: 0 }) || 0}
        </Text>
      ),
    },
    {
      title: "Mounting Charge",
      dataIndex: "mountingCharge",
      key: "mountingCharge",
      align: "right",
      width: 120,
      render: (charge) => (
        <Text type="secondary">
          ₹{charge?.toLocaleString("en-IN", { minimumFractionDigits: 0 }) || 0}
        </Text>
      ),
    },
    {
      title: "Special Rate",
      key: "specialRate",
      align: "right",
      width: 120,
      render: (_, record) => {
        const sum = (record.inventoryAgainst || []).reduce(
          (acc, inv) => acc + (parseFloat(inv?.amount) || 0),
          0
        );
        return (
          <Text type="secondary">
            ₹{sum.toLocaleString("en-IN", { minimumFractionDigits: 0 })}
          </Text>
        );
      },
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right",
      width: 120,
      render: (amount) => (
        <Text strong style={{ color: "#52c41a" }}>
          ₹{amount?.toLocaleString("en-IN", { minimumFractionDigits: 0 })}
        </Text>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      fixed: "right",
      render: (_, record) => {
        return (
          <Button
            type="primary"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleButtonClick(record)}
            style={{ whiteSpace: "nowrap" }}
          >
            Download
          </Button>
        );
      },
    }

  ];

  return (
    <>
      {selectedCreditNote && (
        <PdfFormatJsx
          creditNoteData={selectedCreditNote}
          openFiltersOnLoad
          showLauncherButtons={false}
          onRequestClose={() => setSelectedCreditNote(null)}
        />
      )}
      <Card>
        <Title level={4}>Credit Notes</Title>
        <Table
          columns={columns}
          dataSource={creditNotes}
          rowKey="_id"
          loading={loading}
          expandable={{
            expandedRowRender,
            expandRowByClick: false,
            expandIconColumnIndex: 0,
            expandIcon: ({ expanded, onExpand, record }) => (
              <Button
                type="text"
                onClick={(e) => onExpand(record, e)}
                style={{ padding: "0 8px" }}
              >
                {expanded ? <DownOutlined /> : <RightOutlined />}
              </Button>
            ),
          }}
          pagination={{ pageSize: 10 }}
          bordered
          size="small"
          scroll={{ x: 1240 }}
        />

        <style jsx global>{`
        .ant-table-tbody > tr.child-row:hover > td {
          background-color: #e6f7ff;
        }
        .ant-table-expanded-row::before {
          display: none !important;
        }
        .child-table .ant-table-thead > tr > th {
          background-color: #f0f5ff !important;
        }
      `}</style>
      </Card>
    </>
  );
};

export default GeneratedCreditNotes;
